import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "lazy-chunk-reloaded";
const MAX_RELOADS = 2;

function getErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function isDynamicImportError(err: unknown): boolean {
  const message = getErrorMessage(err);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("module script failed") ||
    message.includes("ChunkLoadError") ||
    message.includes("Loading chunk")
  );
}

function reloadForFreshChunks(): Promise<never> | null {
  if (typeof window === "undefined") return null;

  const attempts = Number(window.sessionStorage.getItem(RELOAD_KEY) || "0");
  if (attempts >= MAX_RELOADS) return null;

  window.sessionStorage.setItem(RELOAD_KEY, String(attempts + 1));
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set("__chunk_retry", String(Date.now()));
  window.location.replace(nextUrl.toString());

  return new Promise<never>(() => {});
}

export function clearDynamicImportRetryState() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(RELOAD_KEY);
  if (window.location.search.includes("__chunk_retry=")) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("__chunk_retry");
    window.history.replaceState(null, "", cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
  }
}

export async function importWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const mod = await factory();
    clearDynamicImportRetryState();
    return mod;
  } catch (err) {
    if (isDynamicImportError(err)) {
      const reload = reloadForFreshChunks();
      if (reload) return reload;
    }
    throw err;
  }
}

/**
 * Wraps React.lazy with automatic recovery for stale dynamic imports.
 * When Vite produces a new chunk hash (after a redeploy or HMR update),
 * the old URL returns a "Failed to fetch dynamically imported module"
 * error. This helper reloads the page once to fetch the fresh manifest.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() => importWithRetry(factory));
}